/**
 * Coups de pouce — attendu 652 (conversions de durées, 4 moteurs).
 *
 * VARIANTES routées par contenu.sousType DIRECTIONNEL (revue 2026-08-01,
 * découpage affiné sur objection utilisateur : « Convertir 5 h 35 min en
 * minutes » recevait la fiche division alors qu'il faut multiplier) :
 *   - simple_multi  → vers la petite unité : multiplication posée (SVG animé)
 *   - simple_divi   → vers la grande unité : division posée (SVG animé)
 *   - chaine        → divisions en chaîne : mêmes pages que simple_divi
 *   - sexadec_vers_dec / sexadec_vers_sexa → page 1 commune (« la virgule
 *     n'est pas des minutes »), page 2 par geste (÷ 60 ou × 60)
 *   - frac_vers_min / min_vers_frac → concept commun (« fraction d'heure =
 *     fraction de 60 »), geste propre (prendre la fraction / mettre sur 60)
 *   - trous guidés : page DÉDIÉE « quotient et reste » (doctrine travail
 *     résiduel : l'énoncé affiche déjà la division à poser) ;
 *   - relier (toujours vers le décimal) → sexadec_vers_dec.
 * JAMAIS la table demi/quart/… en fiche fracmin (pur rappel = réponse).
 * Valeurs d'exemples HORS pools : 92 min (< 125), 9 jours (> 7), 7 h 26 min
 * (h > 5), 9,2 h, 3 h 24 min (24 ∉ {30 15 45 6} ni MINS_DEC), 973 min
 * (> 955), un cinquième d'heure et 12 min (∉ {1/2 1/4 3/4 1/10}).
 */

// Division posée ANIMÉE (idée utilisateur) : la potence 92 ÷ 60 — le quotient
// s'allume, la soustraction se pose, le reste tombe, la conclusion s'écrit.
const SVG_DIVISION = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 150" width="290" height="145" role="img"
     aria-label="Division posée : 92 divisé par 60. Combien de fois 60 dans 92 ? 1 fois. On soustrait 60, il reste 32. Donc 92 minutes égalent 1 heure 32 minutes">
  <style>
    .anim { opacity: 1; }
    @media (prefers-reduced-motion: no-preference) {
      .d1 { opacity: 0; animation: cp652div 6.4s linear infinite 0s; }
      .d2 { opacity: 0; animation: cp652div 6.4s linear infinite 1.6s; }
      .d3 { opacity: 0; animation: cp652div 6.4s linear infinite 3.2s; }
      .d4 { opacity: 0; animation: cp652div 6.4s linear infinite 4.8s; }
    }
    @keyframes cp652div { 0%, 4% { opacity: 0; } 10%, 92% { opacity: 1; } 97%, 100% { opacity: 0; } }
  </style>
  <text x="70" y="30" text-anchor="end" font-size="16" font-weight="bold" fill="#0f172a">92</text>
  <line x1="85" y1="14" x2="85" y2="66" stroke="#0f172a" stroke-width="2"/>
  <line x1="85" y1="36" x2="150" y2="36" stroke="#0f172a" stroke-width="2"/>
  <text x="95" y="30" font-size="16" fill="#0f172a">60</text>
  <g class="anim d1">
    <text x="160" y="22" font-size="11.5" fill="#64748b">combien de fois<tspan x="160" dy="14">60 dans 92 ?</tspan></text>
    <text x="95" y="58" font-size="17" font-weight="bold" fill="#f97316">1</text>
    <text x="160" y="58" font-size="12" font-weight="bold" fill="#f97316">1 fois</text>
  </g>
  <g class="anim d2">
    <text x="70" y="54" text-anchor="end" font-size="14" fill="#0f172a">− 60</text>
    <line x1="30" y1="62" x2="78" y2="62" stroke="#0f172a" stroke-width="1.5"/>
  </g>
  <g class="anim d3">
    <text x="70" y="84" text-anchor="end" font-size="16" font-weight="bold" fill="#fb923c">32</text>
    <text x="80" y="84" font-size="11.5" fill="#fb923c">← le reste</text>
  </g>
  <g class="anim d4">
    <text x="150" y="126" text-anchor="middle" font-size="14.5" font-weight="bold" fill="#0f172a">92 min = <tspan fill="#f97316">1 h</tspan> <tspan fill="#fb923c">32 min</tspan> ✓</text>
  </g>
</svg>`.trim();

// Multiplication ANIMÉE (idée utilisateur) : 7 h 26 min → les heures
// deviennent des minutes (× 60), puis on ajoute les minutes restantes.
const SVG_MULTIPLICATION = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 128" width="290" height="124" role="img"
     aria-label="Convertir 7 heures 26 minutes en minutes : 7 heures fois 60 égalent 420 minutes ; on ajoute les 26 minutes : 420 plus 26 égalent 446 minutes">
  <style>
    .anim { opacity: 1; }
    @media (prefers-reduced-motion: no-preference) {
      .m1 { opacity: 0; animation: cp652mul 5.4s linear infinite 0s; }
      .m2 { opacity: 0; animation: cp652mul 5.4s linear infinite 1.8s; }
      .m3 { opacity: 0; animation: cp652mul 5.4s linear infinite 3.6s; }
    }
    @keyframes cp652mul { 0%, 5% { opacity: 0; } 12%, 90% { opacity: 1; } 96%, 100% { opacity: 0; } }
  </style>
  <rect x="12" y="14" width="58" height="28" rx="6" fill="none" stroke="#0f172a" stroke-width="2"/>
  <text x="41" y="33" text-anchor="middle" font-size="14.5" font-weight="bold" fill="#0f172a">7 h</text>
  <g class="anim m1">
    <line x1="74" y1="28" x2="124" y2="28" stroke="#f97316" stroke-width="2.5"/>
    <polygon points="132,28 122,23 122,33" fill="#f97316"/>
    <text x="103" y="18" text-anchor="middle" font-size="11.5" font-weight="bold" fill="#f97316">× 60</text>
    <rect x="134" y="14" width="78" height="28" rx="6" fill="none" stroke="#f97316" stroke-width="2.5"/>
    <text x="173" y="33" text-anchor="middle" font-size="14.5" font-weight="bold" fill="#f97316">420 min</text>
  </g>
  <g class="anim m2">
    <text x="12" y="66" font-size="12.5" fill="#0f172a">On ajoute les 26 min restantes : 420 + 26.</text>
  </g>
  <g class="anim m3">
    <rect x="12" y="80" width="78" height="28" rx="6" fill="none" stroke="#0f172a" stroke-width="2"/>
    <text x="51" y="99" text-anchor="middle" font-size="14.5" font-weight="bold" fill="#0f172a">446 min</text>
    <text x="98" y="99" font-size="15" font-weight="bold" fill="#f97316">✓</text>
    <text x="115" y="99" font-size="11.5" fill="#64748b">= 7 h 26 min</text>
  </g>
</svg>`.trim();

// Page 1 commune du bloc « table du temps »
const PAGE_TABLE = {
    titre: 'La table du temps',
    lignes: [
        '1 min = **60** s · 1 h = **60** min · 1 j = **24** h · 1 semaine = **7** j.',
        'Vers une unité plus **petite** : multiplie. Vers une plus **grande** : divise, et le reste garde la petite unité.',
    ],
};

const FICHE_SIMPLE_MULTI = {
    pages: [
        PAGE_TABLE,
        {
            titre: 'Poser la multiplication',
            lignes: [
                'Convertir **7 h 26 min** en minutes : chaque heure vaut **60 min**.',
            ],
            svg: SVG_MULTIPLICATION,
        },
    ],
};

const FICHE_SIMPLE_DIVI = {
    pages: [
        PAGE_TABLE,
        {
            titre: 'Poser la division',
            lignes: [
                'Convertir **92 min** en heures : on divise par **60**.',
            ],
            svg: SVG_DIVISION,
        },
    ],
};

// Page 1 commune du bloc décimal ↔ sexagésimal
const PAGE_CENTIEMES = {
    titre: 'Les minutes ne sont pas des centièmes',
    lignes: [
        '2,5 h ne veut **pas** dire 2 h 50 min : la virgule compte des morceaux d\'**heure**, pas des minutes.',
    ],
};

// Conversion de la virgule ANIMÉE (idée utilisateur) : 3,4 h — le piège
// « 3 h 40 » se barre en rouge, la décimale se convertit (0,4 × 60 = 24),
// la vraie heure apparaît. Valeur hors pools : le couple (3 ; 0,4) n'existe
// pas dans EQUIVS (décimales tirées dans {0,5 0,25 0,75 0,1}), et l'exemple
// MIROITE la page « Vers l'écriture décimale » (3 h 24 min ↔ 3,4 h).
const SVG_VIRGULE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 140" width="290" height="136" role="img"
     aria-label="3,4 heures, ce n'est pas 3 heures 40 minutes : la virgule n'est pas des minutes. On convertit la partie décimale : 0,4 fois 60 égale 24 minutes. Donc 3,4 heures égalent 3 heures 24 minutes">
  <style>
    .anim { opacity: 1; }
    @media (prefers-reduced-motion: no-preference) {
      .v1 { opacity: 0; animation: cp652vrg 5.4s linear infinite 0s; }
      .v2 { opacity: 0; animation: cp652vrg 5.4s linear infinite 1.8s; }
      .v3 { opacity: 0; animation: cp652vrg 5.4s linear infinite 3.6s; }
    }
    @keyframes cp652vrg { 0%, 5% { opacity: 0; } 12%, 90% { opacity: 1; } 96%, 100% { opacity: 0; } }
  </style>
  <rect x="12" y="14" width="64" height="28" rx="6" fill="none" stroke="#0f172a" stroke-width="2"/>
  <text x="44" y="33" text-anchor="middle" font-size="14.5" font-weight="bold" fill="#0f172a">3,4 h</text>
  <g class="anim v1">
    <text x="100" y="33" font-size="14" fill="#dc2626">3 h 40 min ?</text>
    <line x1="96" y1="28" x2="188" y2="28" stroke="#dc2626" stroke-width="2.5"/>
    <text x="100" y="50" font-size="11" fill="#64748b">non ! la virgule n'est<tspan x="100" dy="13">pas des minutes</tspan></text>
  </g>
  <g class="anim v2">
    <text x="60" y="78" font-size="13" fill="#0f172a">les 0,4 h : 0,4 × 60 = <tspan font-weight="bold" fill="#f97316">24 min</tspan></text>
  </g>
  <g class="anim v3">
    <line x1="30" y1="46" x2="30" y2="86" stroke="#f97316" stroke-width="2.5"/>
    <polygon points="30,94 25,84 35,84" fill="#f97316"/>
    <rect x="12" y="98" width="86" height="28" rx="6" fill="none" stroke="#f97316" stroke-width="2.5"/>
    <text x="55" y="117" text-anchor="middle" font-size="14" font-weight="bold" fill="#f97316">3 h 24 min</text>
    <text x="106" y="117" font-size="15" font-weight="bold" fill="#f97316">✓</text>
  </g>
</svg>`.trim();

const FICHE_SEXADEC_VERS_SEXA = {
    pages: [
        PAGE_CENTIEMES,
        {
            // SVG seul (règle « le schéma parle pour lui-même »)
            titre: 'Vers les heures et minutes',
            svg: SVG_VIRGULE,
        },
    ],
};

// Miroir de SVG_VIRGULE pour l'autre direction : 3 h 24 min — le piège
// « 3,24 h » se barre, les minutes se divisent (24 ÷ 60 = 0,4), l'écriture
// décimale apparaît. Mêmes valeurs dans les deux sens (demande utilisateur).
const SVG_VIRGULE_INV = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 140" width="290" height="136" role="img"
     aria-label="3 heures 24 minutes, ce n'est pas 3,24 heures : les minutes ne sont pas des centièmes. On divise les minutes par 60 : 24 divisé par 60 égale 0,4. Donc 3 heures 24 minutes égalent 3,4 heures">
  <style>
    .anim { opacity: 1; }
    @media (prefers-reduced-motion: no-preference) {
      .w1 { opacity: 0; animation: cp652vrg2 5.4s linear infinite 0s; }
      .w2 { opacity: 0; animation: cp652vrg2 5.4s linear infinite 1.8s; }
      .w3 { opacity: 0; animation: cp652vrg2 5.4s linear infinite 3.6s; }
    }
    @keyframes cp652vrg2 { 0%, 5% { opacity: 0; } 12%, 90% { opacity: 1; } 96%, 100% { opacity: 0; } }
  </style>
  <rect x="12" y="14" width="86" height="28" rx="6" fill="none" stroke="#0f172a" stroke-width="2"/>
  <text x="55" y="33" text-anchor="middle" font-size="14" font-weight="bold" fill="#0f172a">3 h 24 min</text>
  <g class="anim w1">
    <text x="122" y="33" font-size="14" fill="#dc2626">3,24 h ?</text>
    <line x1="118" y1="28" x2="182" y2="28" stroke="#dc2626" stroke-width="2.5"/>
    <text x="122" y="50" font-size="11" fill="#64748b">non ! les minutes ne sont<tspan x="122" dy="13">pas des centièmes</tspan></text>
  </g>
  <g class="anim w2">
    <text x="70" y="78" font-size="13" fill="#0f172a">les 24 min : 24 ÷ 60 = <tspan font-weight="bold" fill="#f97316">0,4 h</tspan></text>
  </g>
  <g class="anim w3">
    <line x1="30" y1="46" x2="30" y2="86" stroke="#f97316" stroke-width="2.5"/>
    <polygon points="30,94 25,84 35,84" fill="#f97316"/>
    <rect x="12" y="98" width="64" height="28" rx="6" fill="none" stroke="#f97316" stroke-width="2.5"/>
    <text x="44" y="117" text-anchor="middle" font-size="14" font-weight="bold" fill="#f97316">3,4 h</text>
    <text x="84" y="117" font-size="15" font-weight="bold" fill="#f97316">✓</text>
  </g>
</svg>`.trim();

const FICHE_SEXADEC_VERS_DEC = {
    pages: [
        PAGE_CENTIEMES,
        {
            // SVG seul (règle « le schéma parle pour lui-même »)
            titre: 'Vers l\'écriture décimale',
            svg: SVG_VIRGULE_INV,
        },
    ],
};

// Disque d'heure ANIMÉ (idée utilisateur) : 60 min coupées en 5 parts —
// une part s'allume (1/5 = 12 min), puis deux (2/5 = 24 min). Le cinquième
// est HORS pool ({1/2 1/4 3/4 1/10}) : rien n'est soufflé.
const SVG_DISQUE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 132" width="290" height="128" role="img"
     aria-label="Une heure, soit 60 minutes, coupée en 5 parts égales : une part vaut un cinquième d'heure, 60 divisé par 5 égale 12 minutes ; deux parts valent deux cinquièmes, 2 fois 12 égale 24 minutes">
  <style>
    .anim { opacity: 1; }
    @media (prefers-reduced-motion: no-preference) {
      .f1 { opacity: 0; animation: cp652dsq 5.4s linear infinite 0s; }
      .f2 { opacity: 0; animation: cp652dsq 5.4s linear infinite 1.8s; }
      .f3 { opacity: 0; animation: cp652dsq 5.4s linear infinite 3.6s; }
    }
    @keyframes cp652dsq { 0%, 5% { opacity: 0; } 12%, 90% { opacity: 1; } 96%, 100% { opacity: 0; } }
  </style>
  <circle cx="62" cy="66" r="44" fill="none" stroke="#0f172a" stroke-width="2"/>
  <line x1="62" y1="66" x2="62" y2="22" stroke="#94a3b8" stroke-width="1.5"/>
  <line x1="62" y1="66" x2="103.8" y2="52.4" stroke="#94a3b8" stroke-width="1.5"/>
  <line x1="62" y1="66" x2="87.9" y2="101.6" stroke="#94a3b8" stroke-width="1.5"/>
  <line x1="62" y1="66" x2="36.1" y2="101.6" stroke="#94a3b8" stroke-width="1.5"/>
  <line x1="62" y1="66" x2="20.2" y2="52.4" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="125" y="24" font-size="11.5" fill="#64748b">1 heure = 60 minutes,</text>
  <text x="125" y="38" font-size="11.5" fill="#64748b">coupée en 5 parts égales.</text>
  <g class="anim f1">
    <path d="M 62 66 L 62 22 A 44 44 0 0 1 103.8 52.4 Z" fill="#f97316" fill-opacity="0.85" stroke="#f97316"/>
    <text x="125" y="66" font-size="12.5" font-weight="bold" fill="#f97316">une part = 1/5 d'heure</text>
    <text x="125" y="82" font-size="12.5" fill="#0f172a">60 ÷ 5 = <tspan font-weight="bold" fill="#f97316">12 min</tspan></text>
  </g>
  <g class="anim f2">
    <path d="M 62 66 L 103.8 52.4 A 44 44 0 0 1 87.9 101.6 Z" fill="#fb923c" fill-opacity="0.75" stroke="#fb923c"/>
    <text x="125" y="106" font-size="12.5" font-weight="bold" fill="#fb923c">deux parts = 2/5</text>
    <text x="125" y="122" font-size="12.5" fill="#0f172a">2 × 12 = <tspan font-weight="bold" fill="#fb923c">24 min</tspan> ✓</text>
  </g>
</svg>`.trim();

const FICHE_FRAC_VERS_MIN = {
    pages: [
        {
            titre: 'Une fraction d\'heure',
            lignes: [
                'Si on cherche 2/5 d\'heure, on cherche 2/5 de **60 minutes**.',
            ],
            svg: SVG_DISQUE,
        },
    ],
};

const FICHE_MIN_VERS_FRAC = {
    pages: [
        {
            titre: 'Une fraction d\'heure',
            lignes: [
                'Si on cherche 2/5 d\'heure, on cherche 2/5 de **60 minutes**.',
                'Mets les minutes **sur 60**, puis simplifie : 12 min = 12/60 = **1/5** d\'heure.',
            ],
            svg: SVG_DISQUE,
        },
    ],
};

// Page dédiée au format trous guidés : l'énoncé affiche déjà « 973 ÷ 60 = … »,
// le travail résiduel est la division euclidienne elle-même.
const FICHE_QUOTIENT_RESTE = {
    pages: [
        {
            // Rappel de la chaîne d'unités (idée utilisateur 2026-08-01) :
            // c'est elle qui justifie le diviseur affiché par l'énoncé.
            titre: 'La table du temps',
            lignes: [
                '1 semaine = **7** j · 1 j = **24** h · 1 h = **60** min · 1 min = **60** s.',
                'Des jours aux semaines : on **divise** par 7. Des semaines aux jours : on **multiplie** par 7.',
            ],
        },
        {
            titre: 'La division qui convertit',
            lignes: [
                'On veut convertir **973 min** en heures et minutes : on divise par 60.',
                '973 ÷ 60 : combien de **fois** 60 dans 973 ? ==16== fois (16 × 60 = 960), et il **reste** ==13==.',
                'Le quotient devient la grande unité, le reste garde la petite : 973 min = ==16== h ==13== min.',
            ],
        },
    ],
};

const VARIANTES_CONVERSION = {
    variantes: [
        { sousType: 'simple_multi', pages: FICHE_SIMPLE_MULTI.pages },
        { sousType: 'simple_divi', pages: FICHE_SIMPLE_DIVI.pages },
        { sousType: 'chaine', pages: FICHE_SIMPLE_DIVI.pages },
        { sousType: 'sexadec_vers_dec', pages: FICHE_SEXADEC_VERS_DEC.pages },
        { sousType: 'sexadec_vers_sexa', pages: FICHE_SEXADEC_VERS_SEXA.pages },
        { sousType: 'frac_vers_min', pages: FICHE_FRAC_VERS_MIN.pages },
        { sousType: 'min_vers_frac', pages: FICHE_MIN_VERS_FRAC.pages },
    ],
    defaut: FICHE_SIMPLE_DIVI,
};

const CP_652 = {
    conversion_duree_qcm_unique: VARIANTES_CONVERSION,
    conversion_duree_texte: VARIANTES_CONVERSION,
    conversion_duree_texte_trou_saisie: FICHE_QUOTIENT_RESTE,
    conversion_duree_relier: FICHE_SEXADEC_VERS_DEC,
};

export default CP_652;
